import { itemAtivo, navGroups, type OrganizationType } from "@/components/layout/nav-items";
import { marcarRecarga } from "./sessao-visual";

type OrganizacaoAlvo = {
  types?: OrganizationType[] | null;
  isPersonal?: boolean | null;
};

/**
 * Recarrega a página depois de uma troca de organização iniciada pelo usuário.
 *
 * Por que recarga e não só `utils.invalidate()`: invalidar zera o cache de dados, mas não zera
 * estado de componente — diálogo aberto, filtro em memória, seleção corrente. Numa aplicação
 * multi-tenant é justamente aí que dado da organização anterior sobrevive na tela.
 *
 * Só é chamada nos pontos onde trocar de organização É a intenção (seletor da sidebar, página de
 * Organizações, criação). Os fluxos que trocam a organização por dentro de um diálogo — escolher
 * onde criar a consulta/paciente/pedido, e reverter ao cancelar — continuam sem recarga, senão a
 * página morreria no meio do preenchimento.
 *
 * Nota conhecida: recarregar não limpa `localStorage`. Os filtros da agenda (dentistas/serviços)
 * são gravados numa chave global, sem escopo de organização, então ids da organização anterior
 * sobrevivem a isto — é bug separado.
 */
export function recarregarNaOrganizacao(org: OrganizacaoAlvo | null | undefined, destino?: string) {
  if (typeof window === "undefined") return;
  // Deixa a tela de loading dizer o que está acontecendo em vez de "Carregando".
  marcarRecarga("troca-organizacao");
  // Com destino explícito (notificação de outra organização apontando pra uma tela dela), vai
  // direto pra lá — se a rota existir naquela organização. Sem destino, fica onde está.
  const alvo = destino && rotaValida(destino, org) ? destino : null;
  if (alvo) {
    window.location.assign(alvo);
    return;
  }
  const atual = rotaValida(window.location.pathname, org) ? null : "/inicio";
  if (atual) window.location.assign(atual);
  else window.location.reload();
}

/**
 * A rota atual existe pra essa organização? Usa o próprio `navGroups` como fonte de verdade — é
 * ele que já declara `organizationTypes`, `personalOnly` e `teamOnly` por grupo, e duplicar essa
 * tabela aqui garantiria que as duas divergissem na primeira rota nova.
 *
 * Rota que não pertence a nenhum grupo do menu (Início, Notificações, Apps Pro) vale pra qualquer
 * organização. Rota em mais de um grupo (Configurações está nos dois de Gestão) vale se algum
 * deles servir. Minha conta é da pessoa: vale em qualquer organização, mesmo estando só no menu
 * da pessoal.
 */
const DE_QUALQUER_ORGANIZACAO = ["/conta"];

export function rotaValida(pathname: string, org: OrganizacaoAlvo | null | undefined): boolean {
  if (DE_QUALQUER_ORGANIZACAO.some((r) => pathname === r || pathname.startsWith(`${r}/`))) return true;
  const grupos = navGroups.filter((g) =>
    // Inclui as rotas que um item reúne (`ativoEm`): /salas é da clínica pelo Minha clínica.
    g.items.some((item) => itemAtivo(item, pathname)),
  );
  if (grupos.length === 0) return true;

  const tipos = org?.types ?? [];
  return grupos.some((grupo) => {
    if (grupo.organizationTypes && !grupo.organizationTypes.some((t) => tipos.includes(t))) return false;
    if (grupo.personalOnly && !org?.isPersonal) return false;
    if (grupo.teamOnly && org?.isPersonal) return false;
    return true;
  });
}
